import React from 'react';
import {
  Flex,
  GoodsCard3Column,
  GoodsCarousel,
  GoodsCarouselSkeleton,
  Headline,
  Typography,
} from '@banhala/ui-kit';
import { useCodiGoodsList } from './queries/useCodiGoodsList';
import type { CodiGoodsItem } from 'shared/src/api';
import { useLogDisplayGoods } from '../../../../hooks/useLogDisplayGoods';
import { useIsSectionVisible } from '../../store/sectionVisibility';
import { bridge, isNil, useNavigator } from 'shared/src/utils';
import { useRemoveLikeGoods } from '../../queries/useRemoveLikeGoods';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useLikeMap, useAddLikeGoods } from 'shared/src/hooks';
import { useGoodsSno } from '../../hooks/useGoodsSno';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';

const CodiGoodsCarouselContent: React.FC = () => {
  const goodsSno = useGoodsSno();
  const { data: codiGoods } = useCodiGoodsList();
  const logEvent = useLogGoodsAnalyticsEvent();
  const isVisible = useIsSectionVisible('carousel');

  const { logDisplayGoods } = useLogDisplayGoods({
    enabled: isVisible,
  });

  const { likeMap, setLikeMap } = useLikeMap(
    codiGoods.codi_goods?.item_list.reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: val.item.like.is_liked,
      };
    }, {}) ?? {},
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();

  if (isNil(codiGoods.codi_goods)) return null;

  if (codiGoods.codi_goods.item_list.length === 0) return null;

  return (
    <Flex>
      <Headline
        headline={codiGoods.codi_goods.title}
        testID={codiGoods.codi_goods.title}
        size={'medium'}
        topCompact={true}
        rightPanel={
          codiGoods.codi_goods.item_list.length > 9 ? (
            <Flex
              onPress={() => {
                logEvent({ name: 'CLICK_COORDINATED_GOODS_MORE' });
                bridge.openDeeplink(
                  `ably://goods_list/?goods_sno=${goodsSno}&type=coordinated_goods`,
                );
              }}
            >
              <Typography type={'body2'} color={'content_secondary'}>
                전체보기
              </Typography>
            </Flex>
          ) : undefined
        }
      />
      {codiGoods.codi_goods.item_list.length <= 3 ? (
        <GoodsCard3Column<CodiGoodsItem>
          list={codiGoods.codi_goods.item_list}
          listTestID={`LIST_ID=${codiGoods.codi_goods.item_list[0]?.logging.analytics.LIST_ID}`}
          likeMap={likeMap}
          disableVirtualization={true}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            bridge.logAnalyticsEvent({
              name: 'CLICK_GOODS',
              analytics: {
                SCREEN: 'GOODS_DETAIL',
                ...logging.analytics,
                INDEX: index,
              },
            });
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
            });
          }}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              bridge.logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                analytics,
              });
              addLikeGoods(item.sno, {
                SCREEN: 'GOODS_DETAIL',
              });
              return;
            }

            bridge.logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              analytics,
            });

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
          }}
          onViewableItemsChanged={logDisplayGoods}
        />
      ) : (
        <GoodsCarousel<CodiGoodsItem>
          list={codiGoods.codi_goods.item_list}
          listTestID={`LIST_ID=${codiGoods.codi_goods.item_list[0]?.logging.analytics.LIST_ID}`}
          likeMap={likeMap}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            bridge.logAnalyticsEvent({
              name: 'CLICK_GOODS',
              analytics: {
                SCREEN: 'GOODS_DETAIL',
                ...logging.analytics,
                INDEX: index,
              },
            });
            bridge.logAdEvent({ action: 'click', ad: pressed.item.ad });
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
            });
          }}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              bridge.logAdEvent({ action: 'like', ad: pressed.item.ad });

              bridge.logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                analytics,
              });
              addLikeGoods(item.sno, {
                SCREEN: 'GOODS_DETAIL',
              });
              return;
            }
            bridge.logAdEvent({ action: 'unlike', ad: pressed.item.ad });

            bridge.logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              analytics,
            });

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
          }}
          size={'medium'}
          onViewableItemsChanged={logDisplayGoods}
        />
      )}
    </Flex>
  );
};

export const CodiGoodsCarousel: React.FC = () => {
  return (
    <React.Suspense
      fallback={
        <Flex>
          <Flex.Row
            padding={{ horizontal: 16, vertical: 16 }}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Flex
              backgroundColor={'bg_tertiary'}
              width={98}
              height={24}
              borderRadius={4}
            ></Flex>
            <Flex
              backgroundColor={'bg_tertiary'}
              width={64}
              height={18}
              borderRadius={4}
            ></Flex>
          </Flex.Row>
          <GoodsCarouselSkeleton count={4} />
        </Flex>
      }
    >
      <CodiGoodsCarouselContent />
    </React.Suspense>
  );
};
